import {
  Bars3BottomLeftIcon,
  Bars3Icon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronUpDownIcon,
  DocumentDuplicateIcon,
  HashtagIcon,
  IdentificationIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  RectangleStackIcon,
  SparklesIcon,
  TableCellsIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyTypeDefinition } from '../../data/types';

interface PropertyTypeSelectProps {
  id?: string;
  types: PropertyTypeDefinition[];
  value: string;
  onChange: (id: string) => void;
  /**
   * The current value's own definition when it's a real registry type that just isn't offered
   * as a pickable option in `types` (e.g. `List`, restricted to system-property inheritance).
   * Used only to render the closed button's label/icon correctly — never added to the list.
   */
  currentDefinition?: PropertyTypeDefinition;
  /** Shown above the list when the current value isn't in `types` and has no known definition at all (a stale/unknown type). */
  unregisteredLabel?: string;
}

const ICONS: Record<string, typeof Bars3Icon> = {
  Text: Bars3BottomLeftIcon,
  MultilineText: Bars3Icon,
  Number: HashtagIcon,
  Date: CalendarDaysIcon,
  Dropdown: ChevronUpDownIcon,
  YesNo: CheckCircleIcon,
  Project: BriefcaseIcon,
  Contact: IdentificationIcon,
  Image: PhotoIcon,
  Table: TableCellsIcon,
  DocumentDictionary: DocumentDuplicateIcon,
  People: UsersIcon,
  Company: BuildingOffice2Icon,
  ServiceLookup: MagnifyingGlassIcon,
  AIText: SparklesIcon,
  SlideCollection: RectangleStackIcon
};

/** A type is a "lookup" purely by where its value comes from — clientData or an admin/AI service. */
function isLookup(definition: PropertyTypeDefinition): boolean {
  return definition.valueSources.includes('clientData') || definition.valueSources.includes('service');
}

const optionClass =
  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring';

const groupHeaderClass = 'px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground';

/** Replaces a plain `<select>` so lookup types (client-data or service backed) can be grouped and iconed. */
export function PropertyTypeSelect({ id, types, value, onChange, currentDefinition, unregisteredLabel }: PropertyTypeSelectProps) {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapper = useRef<HTMLDivElement>(null);

  const basicTypes = types.filter((definition) => !isLookup(definition));
  const lookupTypes = types.filter(isLookup);
  const ordered = [...basicTypes, ...lookupTypes];

  const selected = types.find((definition) => definition.id === value) ?? currentDefinition;
  const SelectedIcon = selected ? ICONS[selected.id] : undefined;
  const label = selected ? t(`propertyTypes.${selected.id}`) : (unregisteredLabel ?? value);

  useEffect(() => {
    if (!open) return;

    const dismiss = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);

  const select = (definition: PropertyTypeDefinition) => {
    onChange(definition.id);
    setOpen(false);
    setHighlighted(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (ordered.length === 0) return;
      event.preventDefault();
      setOpen(true);
      setHighlighted((current) => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        return (next + ordered.length) % ordered.length;
      });
      return;
    }

    if (event.key === 'Enter' && highlighted >= 0 && ordered[highlighted]) {
      event.preventDefault();
      select(ordered[highlighted]);
    }
  };

  return (
    <div ref={wrapper} className="relative mt-1">
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={onKeyDown}
        className="flex w-full items-center gap-2 rounded-sm border border-input bg-surface px-3 py-2 text-left text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
      >
        {SelectedIcon ? <SelectedIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" /> : null}
        <span className="min-w-0 flex-1 truncate text-foreground">{label}</span>
        <ChevronUpDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-sm border border-border bg-surface shadow-lg"
        >
          {unregisteredLabel ? (
            <li role="presentation" className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
              {unregisteredLabel}
            </li>
          ) : null}

          <li role="presentation" className={groupHeaderClass}>
            {t('properties.typeGroupBasic')}
          </li>
          {basicTypes.map((definition, index) => {
            const Icon = ICONS[definition.id];
            return (
              <li key={definition.id} role="option" id={`${listId}-${index}`} aria-selected={definition.id === value}>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => select(definition)}
                  onMouseEnter={() => setHighlighted(index)}
                  className={`${optionClass} ${highlighted === index ? 'bg-muted' : ''}`}
                >
                  {Icon ? <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" /> : null}
                  <span className="truncate">{t(`propertyTypes.${definition.id}`)}</span>
                </button>
              </li>
            );
          })}

          <li role="presentation" className={`border-t border-border ${groupHeaderClass}`}>
            {t('properties.typeGroupLookup')}
          </li>
          {lookupTypes.map((definition, i) => {
            const index = basicTypes.length + i;
            const Icon = ICONS[definition.id];
            return (
              <li key={definition.id} role="option" id={`${listId}-${index}`} aria-selected={definition.id === value}>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => select(definition)}
                  onMouseEnter={() => setHighlighted(index)}
                  className={`${optionClass} ${highlighted === index ? 'bg-muted' : ''}`}
                >
                  {Icon ? <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" /> : null}
                  <span className="truncate">{t(`propertyTypes.${definition.id}`)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
